import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { setUser } from 'shared/redux/slicers/user.slicer';
import { REPRESENTATIVES_QUERY_STRING } from 'shared/constants/REPRESENTATIVES_QUERY_STRING';
import { useAppSelector } from 'shared/redux/store';
import { representativeService } from 'shared/services/representativesService';
import { useRepresentativesModal } from 'pages/Representatives/context/RepresentativesModalProvider';

const { updateRepresentativeProfile } = representativeService();

type UseRepresentativesListActionProps = {
  currentPrimaryId: string;
};

export const useRepresentativesListAction = ({ currentPrimaryId }: UseRepresentativesListActionProps) => {
  const { handleOpen, handleSetMessage } = useRepresentativesModal();
  // Redux function to edit the user states
  const dispatch = useDispatch();
  const { user: userState } = useAppSelector((state) => state);

  // Loading when changing primary
  const [handleMakePrimaryLoading, setHandleMakePrimaryLoading] = useState<boolean>(false);

  // The current representatives item that is being changed, this is used for loading UI in RepresentativesListItem.tsx
  const [currentProgressId, setCurrentProgressId] = useState<string | null>(null);

  // Query Client for invalidating the representatives list
  const queryClient = useQueryClient();

  // The mutate function for changing the primary representative profile
  const { mutateAsync } = updateRepresentativeProfile();

  // The function for changing the primary Representative Profile
  const handlePrimaryChange = async (profileId: string, primaryImage: string) => {
    // If the currentPrimaryId and the profileId to be changed is the same, simply return because this is the current primary or currentPrimaryId no data
    // If no CurrentPrimaryId simply Return too
    // If loading return too
    if (currentPrimaryId === profileId || !currentPrimaryId || handleMakePrimaryLoading) return;

    // If valid, proceed
    setHandleMakePrimaryLoading(true); // Make the primary loading to true, this is for disabling the primary tag buttons
    setCurrentProgressId(profileId); // The current item that is being changed, this is used for the circular progress bar in Primary Tag in RepresentativesListItem.tsx

    // Make the selected item primary
    await mutateAsync(
      {
        profileId, // The profile that is being changed to primary
        payload: { primary: true }, // Change the primary to true
      },
      {
        onSuccess: async () => {
          // If success invalidate the Representative profile in Representatives List
          await queryClient.invalidateQueries([REPRESENTATIVES_QUERY_STRING.representatives_multiple_profile]);

          // Invalidate the specific profile that is being changed to primary,
          await queryClient.invalidateQueries(
            [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, profileId],
            {
              refetchInactive: true,
            },
          );
          // Invalidate the specific profile that is the current primary,
          await queryClient.invalidateQueries(
            [REPRESENTATIVES_QUERY_STRING.representatives_single_profile, currentPrimaryId],
            {
              refetchInactive: true,
            },
          );

          // Since we are using Redux, change the userState to change the talent profile picture
          dispatch(
            setUser({
              user: {
                ...userState.user,
                attributes: {
                  ...userState.user.attributes,
                  picture: primaryImage || '',
                },
              },
            }),
          );
          // Set the primary loading to false and currentProgresId to null
          setHandleMakePrimaryLoading(false);
          setCurrentProgressId(null);
        },
        onError: () => {
          // if Error change the primary loading to false and currentProgresId to null
          setHandleMakePrimaryLoading(false);
          setCurrentProgressId(null);
          // Open Modal for errors
          handleSetMessage(`Can't proceed this action for now`, 'Please try again later', 'close');
          handleOpen();
        },
      },
    );
  };
  return { handlePrimaryChange, handleMakePrimaryLoading, currentProgressId };
};
